import React, { useState, useEffect } from 'react';
import './chatbots.css';

const AdminChatbot = () => {
    const [activeTab, setActiveTab] = useState('overview');
    const [brainData, setBrainData] = useState(null);
    const [searchUnanswered, setSearchUnanswered] = useState('');
    const [searchLearned, setSearchLearned] = useState('');
    const [newKeywords, setNewKeywords] = useState('');
    const [newResponse, setNewResponse] = useState('');
    const [isLoading, setIsLoading] = useState(false);
    const [error, setError] = useState(null);
    const [editingId, setEditingId] = useState(null);

    const token = localStorage.getItem("token");
    const API_BASE_URL = process.env.REACT_APP_API_URL || 'https://api.gtreksolution.co.ke';

    const loadAll = async () => {
        if (!token) {
            setError('Please login to access admin panel');
            return;
        }

        setIsLoading(true);
        setError(null);

        try {
            const url = `${API_BASE_URL}/api/admin/brain`;
            console.log('Fetching from:', url);

            const response = await fetch(url, {
                headers: {
                    'Authorization': `Bearer ${token}`,
                    'Content-Type': 'application/json'
                }
            });

            if (!response.ok) {
                if (response.status === 401) {
                    throw new Error('Invalid or expired token. Please login again.');
                }
                if (response.status === 404) {
                    throw new Error('API endpoint not found. Please check the URL.');
                }
                throw new Error(`HTTP error! status: ${response.status}`);
            }

            const data = await response.json();
            console.log('Loaded data:', data);

            if (data.success && data.data) {
                setBrainData(data.data);
            } else {
                throw new Error('Invalid data format received');
            }
        } catch (error) {
            console.error('Error loading data:', error);
            setError(error.message);
            setBrainData(null);
        } finally {
            setIsLoading(false);
        }
    };
    // Load data when component mounts
    useEffect(() => {
        if (token) {
            loadAll();
        } else {
            setError('No authentication token found. Please login.');
        }
    }, []);

    const normalizeKeywords = (keywords) => {
        if (!keywords) return [];

        if (Array.isArray(keywords)) return keywords;

        if (typeof keywords === 'string') {
            return keywords.split(',').map(k => k.trim()).filter(Boolean);
        }

        return [];
    };


    const addResponse = async () => {
        if (!newKeywords || !newResponse) {
            alert('Please fill in both keywords and response fields.');
            return;
        }

        setIsLoading(true);
        try {
            const response = await fetch(`${API_BASE_URL}/api/admin/brain/learn`, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    'Authorization': `Bearer ${token}`
                },
                body: JSON.stringify({
                    keywords: newKeywords,
                    response: newResponse
                })
            });

            if (!response.ok) {
                const errorData = await response.json();
                throw new Error(errorData.error || 'Failed to add response');
            }

            setNewKeywords('');
            setNewResponse('');
            await loadAll();
            alert('Response added successfully!');
        } catch (error) {
            console.error('Error adding response:', error);
            alert('Error adding response: ' + error.message);
        } finally {
            setIsLoading(false);
        }
    };

    const toggleActive = async (id) => {
        setIsLoading(true);
        try {
            const response = await fetch(`${API_BASE_URL}/api/admin/brain/learn/${id}/toggle`, {
                method: 'PATCH',
                headers: {
                    'Authorization': `Bearer ${token}`,
                    'Content-Type': 'application/json'
                }
            });

            if (!response.ok) {
                throw new Error('Failed to toggle status');
            }

            await loadAll();
        } catch (error) {
            console.error('Error toggling status:', error);
            alert('Error toggling status: ' + error.message);
        } finally {
            setIsLoading(false);
        }
    };

    const deleteResponse = async (id) => {
        if (!window.confirm('Delete this response?')) return;

        setIsLoading(true);
        try {
            const response = await fetch(`${API_BASE_URL}/api/admin/brain/learn/${id}`, {
                method: 'DELETE',
                headers: { 'Authorization': `Bearer ${token}` }
            });

            if (!response.ok) {
                throw new Error('Failed to delete response');
            }

            await loadAll();
            alert('Response deleted successfully!');
        } catch (error) {
            console.error('Error deleting response:', error);
            alert('Error deleting response: ' + error.message);
        } finally {
            setIsLoading(false);
        }
    };

    const resolveUnanswered = async (id) => {
        try {
            const response = await fetch(`${API_BASE_URL}/api/admin/brain/unanswered/${id}/resolve`, {
                method: 'PATCH',
                headers: {
                    'Authorization': `Bearer ${token}`,
                    'Content-Type': 'application/json'
                }
            });

            if (!response.ok) {
                throw new Error('Failed to resolve question');
            }

            await loadAll();
            return true;
        } catch (error) {
            console.error('Error resolving question:', error);
            alert('Error resolving question: ' + error.message);
            return false;
        }
    };

    // Fixed: Teach from unanswered - separate the resolve and tab switch
    const teachFromUnanswered = async (id, message) => {
        // First, resolve the unanswered question
        const resolved = await resolveUnanswered(id);

        if (resolved) {
            // Then set the form fields
            setNewKeywords(message);
            setNewResponse('');
            setEditingId(null);
            // Switch to learned tab
            setActiveTab('learned');
            // Scroll to the form
            setTimeout(() => {
                const formElement = document.querySelector('.add-response-form');
                if (formElement) {
                    formElement.scrollIntoView({ behavior: 'smooth', block: 'start' });
                }
            }, 100);
        }
    };

    // Edit existing response
    const handleEditResponse = (response) => {
        setEditingId(response?.id || null);

        setNewKeywords(
            Array.isArray(response?.keywords)
                ? response.keywords.join(', ')
                : String(response?.keywords || '')
        );

        setNewResponse(String(response?.response || ''));

        const formElement = document.querySelector('.add-response-form');

        if (formElement) {
            formElement.scrollIntoView({
                behavior: 'smooth',
                block: 'start'
            });
        }
    };

    // Update existing response
    const updateResponse = async () => {
        if (!newKeywords || !newResponse) {
            alert('Please fill in both keywords and response fields.');
            return;
        }

        setIsLoading(true);
        try {
            const response = await fetch(`${API_BASE_URL}/api/admin/brain/learn/${editingId}`, {
                method: 'PUT',
                headers: {
                    'Content-Type': 'application/json',
                    'Authorization': `Bearer ${token}`
                },
                body: JSON.stringify({
                    keywords: newKeywords,
                    response: newResponse
                })
            });

            if (!response.ok) {
                throw new Error('Failed to update response');
            }

            setNewKeywords('');
            setNewResponse('');
            setEditingId(null);
            await loadAll();
            alert('Response updated successfully!');
        } catch (error) {
            console.error('Error updating response:', error);
            alert('Error updating response: ' + error.message);
        } finally {
            setIsLoading(false);
        }
    };

    const cancelEdit = () => {
        setEditingId(null);
        setNewKeywords('');
        setNewResponse('');
    };

    const filteredUnanswered = () => {
        const unanswered = brainData?.top_unanswered || [];

        return unanswered.filter((u) =>
            String(u?.message || '')
                .toLowerCase()
                .includes((searchUnanswered || '').toLowerCase())
        );
    };

    const filteredLearned = () => {
        const responses = brainData?.learned_responses || [];

        return responses.filter((r) => {
            const keywords = Array.isArray(r?.keywords)
                ? r.keywords.join(' ')
                : String(r?.keywords || '');

            const response = String(r?.response || '');

            return (
                keywords.toLowerCase().includes((searchLearned || '').toLowerCase()) ||
                response.toLowerCase().includes((searchLearned || '').toLowerCase())
            );
        });
    };

    const renderStats = () => {
        if (!brainData) return null;

        const stats = [
            { label: 'Learned Responses', value: brainData.active_learned || 0 },
            { label: 'Unanswered Pending', value: brainData.unresolved_unanswered || 0 },
            { label: 'Positive Feedback', value: brainData.positive_feedback || 0 },
            { label: 'Negative Feedback', value: (brainData.total_feedback || 0) - (brainData.positive_feedback || 0) },
            {
                label: 'Satisfaction Rate',
                value: brainData.total_feedback > 0
                    ? Math.round((brainData.positive_feedback / brainData.total_feedback) * 100)
                    : 0,
                suffix: '%'
            }
        ];

        return (
            <div className="stats-grid" style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: '1rem', marginBottom: '2rem' }}>
                {stats.map((stat, idx) => (
                    <div key={idx} className="stat-card" style={{ padding: '1rem', backgroundColor: '#f8f9fa', borderRadius: '8px', textAlign: 'center' }}>
                        <div style={{ fontSize: '2rem', fontWeight: 'bold', color: '#007bff' }}>{stat.value}{stat.suffix || ''}</div>
                        <div style={{ color: '#6c757d' }}>{stat.label}</div>
                    </div>
                ))}
            </div>
        );
    };

    if (isLoading && !brainData) {
        return (
            <div className="container py-5">
                <div className="text-center">
                    <div className="spinner-border text-primary" role="status">
                        <span className="visually-hidden">Loading...</span>
                    </div>
                    <p className="mt-3">Loading chatbot brain data...</p>
                </div>
            </div>
        );
    }

    if (error && !brainData) {
        return (
            <div className="container py-5">
                <div className="alert alert-danger">
                    <h4>Error Loading Data</h4>
                    <p>{error}</p>
                    <button className="btn btn-primary me-2" onClick={loadAll}>
                        Retry
                    </button>
                    <button className="btn btn-secondary" onClick={() => window.location.reload()}>
                        Reload Page
                    </button>
                </div>
            </div>
        );
    }

    return (
        <div className="container py-5">
            <div className="d-flex justify-content-between align-items-center mb-4">
                <h1>🧠 PrimeDale Chatbot Brain</h1>
                <div>
                    <span className="badge bg-secondary me-2">
                        API: {API_BASE_URL}
                    </span>
                    <button className="btn btn-outline-primary btn-sm" onClick={loadAll} disabled={isLoading}>
                        {isLoading ? 'Refreshing...' : 'Refresh Data'}
                    </button>
                </div>
            </div>

            <div className="tabs" style={{ display: 'flex', gap: '0.5rem', marginBottom: '2rem', borderBottom: '1px solid #dee2e6' }}>
                <div
                    className={`tab ${activeTab === 'overview' ? 'active' : ''}`}
                    onClick={() => setActiveTab('overview')}
                    style={{ padding: '0.5rem 1rem', cursor: 'pointer', borderBottom: activeTab === 'overview' ? '2px solid #007bff' : 'none', fontWeight: activeTab === 'overview' ? 'bold' : 'normal' }}
                >
                    Overview
                </div>
                <div
                    className={`tab ${activeTab === 'unanswered' ? 'active' : ''}`}
                    onClick={() => setActiveTab('unanswered')}
                    style={{ padding: '0.5rem 1rem', cursor: 'pointer', borderBottom: activeTab === 'unanswered' ? '2px solid #007bff' : 'none', fontWeight: activeTab === 'unanswered' ? 'bold' : 'normal' }}
                >
                    Unanswered
                    {brainData && brainData.unresolved_unanswered > 0 && (
                        <span className="badge bg-danger ms-2">{brainData.unresolved_unanswered}</span>
                    )}
                </div>
                <div
                    className={`tab ${activeTab === 'learned' ? 'active' : ''}`}
                    onClick={() => setActiveTab('learned')}
                    style={{ padding: '0.5rem 1rem', cursor: 'pointer', borderBottom: activeTab === 'learned' ? '2px solid #007bff' : 'none', fontWeight: activeTab === 'learned' ? 'bold' : 'normal' }}
                >
                    Learned Responses
                </div>
                <div
                    className={`tab ${activeTab === 'feedback' ? 'active' : ''}`}
                    onClick={() => setActiveTab('feedback')}
                    style={{ padding: '0.5rem 1rem', cursor: 'pointer', borderBottom: activeTab === 'feedback' ? '2px solid #007bff' : 'none', fontWeight: activeTab === 'feedback' ? 'bold' : 'normal' }}
                >
                    Feedback
                </div>
            </div>

            <div className="content">
                {/* Overview Tab */}
                {activeTab === 'overview' && (
                    <div className="panel">
                        {renderStats()}

                        <div className="card mt-4">
                            <h3>🔥 Top Unanswered Questions</h3>
                            <div className="table-responsive">
                                <table className="table table-striped">
                                    <thead>
                                        <tr>
                                            <th>Question</th>
                                            <th>Times Asked</th>
                                            <th>Last Seen</th>
                                            <th>Action</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {brainData && brainData.top_unanswered && brainData.top_unanswered.length > 0 ? (
                                            brainData.top_unanswered.slice(0, 5).map(u => (
                                                <tr key={u.id}>
                                                    <td>{u.message}</td>
                                                    <td><span className="badge bg-warning">{u.count}x</span></td>
                                                    <td>{new Date(u.last_seen).toLocaleDateString()}</td>
                                                    <td>
                                                        <button
                                                            className="btn btn-primary btn-sm"
                                                            onClick={() => teachFromUnanswered(u.id, u.message)}
                                                            disabled={isLoading}
                                                        >
                                                            Teach
                                                        </button>
                                                    </td>
                                                </tr>
                                            ))
                                        ) : (
                                            <tr>
                                                <td colSpan="4" className="text-center">No unanswered questions yet</td>
                                            </tr>
                                        )}
                                    </tbody>
                                </table>
                            </div>
                        </div>
                    </div>
                )}

                {/* Unanswered Tab */}
                {activeTab === 'unanswered' && (
                    <div className="panel">
                        <div className="alert alert-info">
                            These are messages that fell through to the fallback. Teach the bot by adding a response.
                        </div>
                        <input
                            className="form-control mb-3"
                            placeholder="Search unanswered..."
                            value={searchUnanswered}
                            onChange={(e) => setSearchUnanswered(e.target.value)}
                        />
                        <div className="table-responsive">
                            <table className="table table-striped">
                                <thead>
                                    <tr>
                                        <th>Message</th>
                                        <th>Count</th>
                                        <th>First Seen</th>
                                        <th>Status</th>
                                        <th>Actions</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {filteredUnanswered().length > 0 ? (
                                        filteredUnanswered().map(u => (
                                            <tr key={u.id}>
                                                <td>{u.message}</td>
                                                <td><span className="badge bg-warning">{u.count}x</span></td>
                                                <td>{new Date(u.first_seen).toLocaleDateString()}</td>
                                                <td>
                                                    <span className={`badge ${u.resolved ? 'bg-success' : 'bg-danger'}`}>
                                                        {u.resolved ? 'Resolved' : 'Pending'}
                                                    </span>
                                                </td>
                                                <td>
                                                    <button
                                                        className="btn btn-primary btn-sm me-2"
                                                        onClick={() => teachFromUnanswered(u.id, u.message)}
                                                        disabled={isLoading}
                                                    >
                                                        Teach
                                                    </button>
                                                    {!u.resolved && (
                                                        <button
                                                            className="btn btn-success btn-sm"
                                                            onClick={() => resolveUnanswered(u.id)}
                                                            disabled={isLoading}
                                                        >
                                                            Resolve
                                                        </button>
                                                    )}
                                                </td>
                                            </tr>
                                        ))
                                    ) : (
                                        <tr>
                                            <td colSpan="5" className="text-center">No unanswered questions</td>
                                        </tr>
                                    )}
                                </tbody>
                            </table>
                        </div>
                    </div>
                )}

                {/* Learned Responses Tab */}
                {activeTab === 'learned' && (
                    <div className="panel">
                        <div className="card mb-4 add-response-form">
                            <div className="card-body">
                                <h3>{editingId ? '✏️ Edit Response' : '➕ Add New Response'}</h3>
                                <div className="mb-3">
                                    <label className="form-label">Keywords (comma-separated)</label>
                                    <input
                                        className="form-control"
                                        placeholder="e.g., safari cost, how much safari, price safari"
                                        value={newKeywords}
                                        onChange={(e) => setNewKeywords(e.target.value)}
                                    />
                                    <small className="text-muted">Separate multiple keywords with commas</small>
                                </div>
                                <div className="mb-3">
                                    <label className="form-label">Response</label>
                                    <textarea
                                        className="form-control"
                                        placeholder="Response the bot should give..."
                                        value={newResponse}
                                        onChange={(e) => setNewResponse(e.target.value)}
                                        rows="4"
                                    />
                                </div>
                                <div>
                                    {editingId ? (
                                        <>
                                            <button className="btn btn-primary me-2" onClick={updateResponse} disabled={isLoading}>
                                                {isLoading ? 'Updating...' : 'Update Response'}
                                            </button>
                                            <button className="btn btn-secondary" onClick={cancelEdit}>
                                                Cancel
                                            </button>
                                        </>
                                    ) : (
                                        <button className="btn btn-primary" onClick={addResponse} disabled={isLoading}>
                                            {isLoading ? 'Adding...' : 'Add Response'}
                                        </button>
                                    )}
                                </div>
                            </div>
                        </div>

                        <input
                            className="form-control mb-3"
                            placeholder="Search learned responses..."
                            value={searchLearned}
                            onChange={(e) => setSearchLearned(e.target.value)}
                        />

                        <div className="table-responsive">
                            <table className="table table-striped">
                                <thead>
                                    <tr>
                                        <th>Keywords</th>
                                        <th>Response</th>
                                        <th>👍</th>
                                        <th>👎</th>
                                        <th>Status</th>
                                        <th>Actions</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {filteredLearned().length > 0 ? (
                                        filteredLearned().map(r => (
                                            <tr key={r.id}>
                                                <td style={{ maxWidth: '200px' }}>{normalizeKeywords(r.keywords).join(', ')}</td>
                                                <td className="response-text" title={r.response}>
                                                    {r.response.substring(0, 80)}...
                                                </td>
                                                <td><span className="badge bg-success">👍 {r.helpful_votes || 0}</span></td>
                                                <td><span className="badge bg-danger">👎 {r.unhelpful_votes || 0}</span></td>
                                                <td>
                                                    <span className={`badge ${r.active ? 'bg-success' : 'bg-secondary'}`}>
                                                        {r.active ? 'Active' : 'Off'}
                                                    </span>
                                                </td>
                                                <td>
                                                    <button
                                                        className="btn btn-warning btn-sm me-2"
                                                        onClick={() => handleEditResponse(r)}
                                                        disabled={isLoading}
                                                    >
                                                        Edit
                                                    </button>
                                                    <button
                                                        className="btn btn-info btn-sm me-2"
                                                        onClick={() => toggleActive(r.id)}
                                                        disabled={isLoading}
                                                    >
                                                        Toggle
                                                    </button>
                                                    <button
                                                        className="btn btn-danger btn-sm"
                                                        onClick={() => deleteResponse(r.id)}
                                                        disabled={isLoading}
                                                    >
                                                        Delete
                                                    </button>
                                                </td>
                                            </tr>
                                        ))
                                    ) : (
                                        <tr>
                                            <td colSpan="6" className="text-center">No learned responses yet</td>
                                        </tr>
                                    )}
                                </tbody>
                            </table>
                        </div>
                    </div>
                )}

                {/* Feedback Tab */}
                {activeTab === 'feedback' && (
                    <div className="panel">
                        <div className="card">
                            <div className="card-body">
                                <h3>📊 Recent Feedback</h3>
                                <div className="table-responsive">
                                    <table className="table table-striped">
                                        <thead>
                                            <tr>
                                                <th>Message</th>
                                                <th>Verdict</th>
                                                <th>Time</th>
                                            </tr>
                                        </thead>
                                        <tbody>
                                            {brainData && brainData.recent_feedback && brainData.recent_feedback.length > 0 ? (
                                                brainData.recent_feedback.map((f, idx) => (
                                                    <tr key={idx}>
                                                        <td>{f.message || '—'}</td>
                                                        <td>
                                                            <span className={`badge ${f.helpful ? 'bg-success' : 'bg-danger'}`}>
                                                                {f.helpful ? '👍 Helpful' : '👎 Not helpful'}
                                                            </span>
                                                        </td>
                                                        <td>{new Date(f.timestamp).toLocaleString()}</td>
                                                    </tr>
                                                ))
                                            ) : (
                                                <tr>
                                                    <td colSpan="3" className="text-center">No feedback yet</td>
                                                </tr>
                                            )}
                                        </tbody>
                                    </table>
                                </div>
                            </div>
                        </div>
                    </div>
                )}
            </div>
        </div>
    );
};

export default AdminChatbot;